import { FormEvent, useEffect, useState } from "react";
import Modal from "../ui/Modal";
import {
  CompetitorOut,
  CompetitorUpdateInput,
  PlatformEnum,
} from "../../types/competitors";
import { updateCompetitor } from "../../api/competitors";
import {
  ClipboardList,
  Link2,
  MapPin,
  Star,
  BarChart,
  Hash,
  BedDouble,
  Building2,
  UsersRound,
} from "lucide-react";

const PLATFORM_OPTIONS: { label: string; value: PlatformEnum }[] = [
  { label: "Броневик", value: "Броневик" },
  { label: "Яндекс", value: "Яндекс" },
  { label: "Островок", value: "Островок" },
  { label: "Другое", value: "other" },
];

type Props = {
  open: boolean;
  onClose: () => void;
  competitor: CompetitorOut | null;
  companyId?: number | null;
  onUpdated: (competitor: CompetitorOut) => void;
};

export default function EditCompetitorModal({
  open,
  onClose,
  competitor,
  companyId,
  onUpdated,
}: Props) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [platform, setPlatform] = useState<PlatformEnum>("Броневик");
  const [stars, setStars] = useState("");
  const [reviewRating, setReviewRating] = useState("");
  const [reviewCount, setReviewCount] = useState("");
  const [address, setAddress] = useState("");
  const [roomCount, setRoomCount] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const baseInputClass =
    "w-full rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-[14px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:border-indigo-300 focus:ring-4 focus:ring-indigo-200/60 disabled:opacity-60 disabled:cursor-not-allowed";

  useEffect(() => {
    if (!open || !competitor) {
      setName("");
      setUrl("");
      setPlatform("Броневик");
      setStars("");
      setReviewRating("");
      setReviewCount("");
      setAddress("");
      setRoomCount("");
      setFormError("");
      setSubmitting(false);
      return;
    }

    setName(competitor.name || "");
    setUrl(competitor.url || "");
    setPlatform(competitor.platform || "Броневик");
    setStars(
      typeof competitor.stars === "number" && Number.isFinite(competitor.stars)
        ? String(competitor.stars)
        : "",
    );
    setReviewRating(
      typeof competitor.review_rating === "number" && Number.isFinite(competitor.review_rating)
        ? String(competitor.review_rating)
        : "",
    );
    setReviewCount(
      typeof competitor.review_count === "number" && Number.isFinite(competitor.review_count)
        ? String(competitor.review_count)
        : "",
    );
    setAddress(competitor.address || "");
    setRoomCount(
      typeof competitor.room_count === "number" && Number.isFinite(competitor.room_count)
        ? String(competitor.room_count)
        : "",
    );
    setFormError("");
    setSubmitting(false);
  }, [open, competitor]);

  if (!competitor) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError("");

    if (!name.trim()) return setFormError("Введите название конкурента");

    const trimmedUrl = url.trim();
    if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
      return setFormError("Ссылка должна начинаться с http:// или https://");
    }

    const payload: CompetitorUpdateInput = {
      name: name.trim(),
      platform,
      url: trimmedUrl || null,
      stars: stars !== "" ? Number(stars) : null,
      review_rating: reviewRating !== "" ? Number(reviewRating) : null,
      review_count: reviewCount !== "" ? Number(reviewCount) : null,
      address: address.trim() || null,
      room_count: roomCount !== "" ? Number(roomCount) : null,
    };

    const { stars: starsValue, review_rating: ratingValue, review_count: countValue, room_count: roomCountValue } = payload;

    if (starsValue !== null && starsValue !== undefined && (starsValue < 0 || starsValue > 5)) {
      return setFormError("Количество звёзд должно быть от 0 до 5");
    }
    if (ratingValue !== null && ratingValue !== undefined && (ratingValue < 0 || ratingValue > 10)) {
      return setFormError("Рейтинг отзывов должен быть в диапазоне 0-10");
    }
    if (countValue !== null && countValue !== undefined && countValue < 0) {
      return setFormError("Количество отзывов не может быть отрицательным");
    }
    if (roomCountValue !== null && roomCountValue !== undefined && roomCountValue < 0) {
      return setFormError("Количество номеров не может быть отрицательным");
    }

    const resolvedCompanyId = competitor!.company_id ?? companyId;
    if (!resolvedCompanyId) {
      return setFormError("Не удалось определить компанию конкурента");
    }

    setSubmitting(true);
    try {
      const updated = await updateCompetitor(
        resolvedCompanyId,
        competitor!.company_hotel_id,
        competitor!.id,
        payload,
      );
      onUpdated(updated);
      onClose();
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || "Не удалось обновить конкурента");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-3xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
            <UsersRound size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Редактировать конкурента</h3>
            <p className="mt-1 text-sm text-slate-500">
              Обновите карточку, чтобы данные отображались верно во всех отчётах
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/70 bg-white/80 px-4 py-3 text-sm text-slate-600 shadow-inner">
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              <Building2 size={14} className="text-slate-500" />
              {competitor.company_name || "—"}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-700">
              <BedDouble size={14} className="text-slate-500" />
              {competitor.company_hotel_name || competitor.hotel_name || competitor.company_hotel_id}
            </span>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Название *</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={baseInputClass}
              placeholder="Например, Hotel Plaza"
            />
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Платформа *</span>
            <div className="relative">
              <ClipboardList size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value as PlatformEnum)}
                className={`${baseInputClass} appearance-none pl-11 pr-10`}
              >
                {PLATFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Ссылка</span>
            <div className="relative">
              <Link2 size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="https://…"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Адрес</span>
            <div className="relative">
              <MapPin size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Город, улица"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Звёзды</span>
            <div className="relative">
              <Star size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={stars}
                onChange={(e) => setStars(e.target.value)}
                type="number"
                min={0}
                max={5}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="0-5"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Рейтинг отзывов</span>
            <div className="relative">
              <BarChart size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={reviewRating}
                onChange={(e) => setReviewRating(e.target.value)}
                type="number"
                step="0.1"
                min={0}
                max={10}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, 8.4"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Количество отзывов</span>
            <div className="relative">
              <Hash size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={reviewCount}
                onChange={(e) => setReviewCount(e.target.value)}
                type="number"
                min={0}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, 123"
              />
            </div>
          </label>

          <label className="block space-y-2">
            <span className="text-sm font-semibold text-slate-600">Количество номеров</span>
            <div className="relative">
              <Hash size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={roomCount}
                onChange={(e) => setRoomCount(e.target.value)}
                type="number"
                min={0}
                className={`${baseInputClass} pl-11 pr-4`}
                placeholder="Например, 50"
              />
            </div>
          </label>
        </div>

        {formError && (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {formError}
          </div>
        )}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:opacity-50 sm:flex-none"
          >
            {submitting ? "Сохраняем…" : "Сохранить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
